/**
 * HQ 게이트 관리 서비스 (본부 전용 — 백엔드에서 hq_admin 게이트)
 *
 * 게이트는 매장 내부망(사설 IP)이라 서버가 먼저 말을 걸 수 없다.
 * 게이트가 서버로 롱폴링하며 기다리고, 여기서 보낸 명령을 가져간다.
 */
import hqApi from './hqApiClient';

export interface StoreGate {
  organization_id: string;
  organization_name: string;
  has_token: boolean;        // 매장 열쇠 발급 여부
  gate_linked: boolean;      // 지금 서버에 붙어 있는지
  gate_version?: string | null;
  seconds_ago?: number | null;
  esp32_entry_ip?: string | null;
  last_seen_at?: string | null;
  /** ready | update_ready | offline | legacy | no_gate */
  status: string;
  active_version?: string | null;
}

export interface GateFirmware {
  id: number;
  version: string;
  notes?: string | null;
  size_bytes: number;
  sha256: string;
  is_active: boolean;
  uploaded_by?: string | null;
  created_at?: string | null;
  activated_at?: string | null;
}

const hqGateService = {
  async listStores(): Promise<StoreGate[]> {
    const { data } = await hqApi.get<StoreGate[]>('/api/v1/gate/admin/stores');
    return data;
  },

  /** 매장 열쇠를 보여준다. 없으면 만든다. rotate=true 면 새로 발급(게이트에 다시 넣어야 함). */
  async issueToken(orgId: string, rotate = false) {
    const { data } = await hqApi.post(
      `/api/v1/gate/admin/stores/${orgId}/token`, null, { params: { rotate } },
    );
    return data as { organization_id: string; organization_name: string; gate_token: string; created: boolean };
  },

  async openGate(orgId: string, direction: 'entry' | 'exit' = 'entry') {
    const { data } = await hqApi.post(`/api/v1/gate/admin/stores/${orgId}/open`, {
      direction, duration_ms: 5000, target_role: direction, reason: '본부 지원',
    });
    return data;
  },

  async listFirmwares(): Promise<GateFirmware[]> {
    const { data } = await hqApi.get<GateFirmware[]>('/api/v1/gate/firmware');
    return data;
  },

  async uploadFirmware(file: File, version: string, notes: string, activate: boolean) {
    const form = new FormData();
    form.append('file', file);
    form.append('version', version);
    form.append('notes', notes);
    form.append('activate', String(activate));
    const { data } = await hqApi.post('/api/v1/gate/firmware', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data as GateFirmware;
  },

  async activate(id: number) {
    const { data } = await hqApi.post(`/api/v1/gate/firmware/${id}/activate`);
    return data as GateFirmware;
  },

  async stopRollout() {
    const { data } = await hqApi.post('/api/v1/gate/firmware/stop');
    return data as { ok: boolean; deactivated: number };
  },
};

export default hqGateService;
